import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  PlatformApiError,
  type ApplicationChange,
  type ApplicationDetail,
  type ApplicationNote,
  type Viewer,
} from '@sfc/sdk';
import { platform } from '../api/client';
import { DetailSection } from '../ui/DetailPane';
import { dateTime } from '../ui/format';

/** Mirrors the transitions the API enforces. The API is still the authority. */
const NEXT_STATUSES: Record<string, string[]> = {
  received: ['in_review', 'shortlisted', 'rejected', 'withdrawn'],
  in_review: ['shortlisted', 'rejected', 'withdrawn'],
  shortlisted: ['offered', 'in_review', 'rejected', 'withdrawn'],
  offered: ['hired', 'shortlisted', 'rejected', 'withdrawn'],
  hired: [],
  rejected: [],
  withdrawn: [],
};

const CORRECTABLE = ['firstName', 'lastName', 'email', 'phone', 'linkedin'] as const;

interface ReviewControlsProps {
  viewer: Viewer;
  detail: ApplicationDetail;
  /** Called after anything that changes the record, so the caller reloads. */
  onChanged: () => void;
  onErased: () => void;
}

/**
 * Everything a recruiter does to an application once they have read it.
 *
 * Every write carries the version the screen was drawn from. If somebody else
 * has touched the record since, the API refuses and this says so rather than
 * quietly winning the race.
 */
export function ReviewControls({ viewer, detail, onChanged, onErased }: ReviewControlsProps): React.JSX.Element {
  const organizationId = viewer.organization.id;
  const mayWrite = viewer.capabilities.includes('applications:write');
  const mayErase = viewer.capabilities.includes('applications:delete');

  const [notes, setNotes] = useState<ApplicationNote[]>([]);
  const [changes, setChanges] = useState<ApplicationChange[]>([]);
  const [noteBody, setNoteBody] = useState('');
  const [field, setField] = useState<(typeof CORRECTABLE)[number]>('email');
  const [value, setValue] = useState('');
  const [problem, setProblem] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  const reload = useCallback(
    async (signal?: AbortSignal) => {
      const [loadedNotes, loadedChanges] = await Promise.all([
        platform.review.listNotes(organizationId, detail.id, signal),
        platform.review.listChanges(organizationId, detail.id, signal),
      ]);
      setNotes(loadedNotes);
      setChanges(loadedChanges);
    },
    [organizationId, detail.id],
  );

  useEffect(() => {
    const controller = new AbortController();
    void reload(controller.signal).catch(() => undefined);
    return () => controller.abort();
  }, [reload]);

  async function attempt(work: () => Promise<unknown>, after: () => void = onChanged): Promise<void> {
    setProblem(undefined);
    setBusy(true);
    try {
      await work();
      after();
    } catch (error) {
      setProblem(describe(error));
    } finally {
      setBusy(false);
    }
  }

  async function correct(event: FormEvent): Promise<void> {
    event.preventDefault();
    await attempt(async () => {
      await platform.review.correct(organizationId, detail.id, detail.version, { [field]: value });
      setValue('');
      await reload();
    });
  }

  async function addNote(event: FormEvent): Promise<void> {
    event.preventDefault();
    await attempt(async () => {
      await platform.review.addNote(organizationId, detail.id, noteBody);
      setNoteBody('');
      await reload();
    }, () => undefined);
  }

  // Every correction, kept with what it replaced.
  const changeList =
    changes.length > 0 ? (
      <ul className="checks checks--compact" data-testid="application-changes">
        {changes.map((change) => (
          <li key={change.id} className="checks__item">
            <span className="member">
              <span className="member__name">{change.field}</span>
              <span className="member__email">
                {change.previousValue || '—'} → {change.newValue || '—'}
              </span>
            </span>
            <time className="member__email" dateTime={change.changedAt}>
              {dateTime(change.changedAt)}
            </time>
          </li>
        ))}
      </ul>
    ) : null;

  return (
    <div className="review">
      <DetailSection
        title="Stage"
        action={
          <span className="pill pill--pending" data-testid="application-status">
            {detail.status.replace(/_/g, ' ')}
          </span>
        }
      >
        {problem ? (
          <p className="notice notice--problem" role="alert" data-testid="review-problem">
            {problem}
          </p>
        ) : null}

        {mayWrite && NEXT_STATUSES[detail.status]?.length ? (
          <div className="field">
            <label htmlFor="application-status">Move to</label>
            <select
              id="application-status"
              disabled={busy}
              value=""
              onChange={(event) =>
                void attempt(() =>
                  platform.review.changeStatus(organizationId, detail.id, detail.version, event.target.value),
                )
              }
            >
              <option value="" disabled>
                Choose a status
              </option>
              {NEXT_STATUSES[detail.status]!.map((status) => (
                <option key={status} value={status}>
                  {status.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <p className="notice">
            {mayWrite ? 'This application’s process has ended.' : 'Your role may not change this.'}
          </p>
        )}
      </DetailSection>

      <DetailSection title="Internal notes">
        <ul className="notes" data-testid="application-notes">
          {notes.length === 0 ? <li className="notice">No notes yet.</li> : null}
          {notes.map((note) => (
            <li key={note.id} className="note">
              <p className="note__body">{note.body}</p>
              <time className="row__sub" dateTime={note.createdAt}>
                {dateTime(note.createdAt)}
              </time>
            </li>
          ))}
        </ul>

        {mayWrite ? (
          <form className="compose" onSubmit={addNote}>
            <div className="field">
              <label htmlFor="application-note">Add an internal note</label>
              <textarea
                id="application-note"
                rows={2}
                required
                value={noteBody}
                onChange={(event) => setNoteBody(event.target.value)}
              />
            </div>
            <div className="compose__foot">
              <span className="row__sub">Never shown to the candidate.</span>
              <button className="button button--small" type="submit" disabled={busy}>
                Add note
              </button>
            </div>
          </form>
        ) : null}
      </DetailSection>

      {mayWrite ? (
        <DetailSection title="Fix a detail">
          <form className="inline-form" onSubmit={correct}>
            <div className="field">
              <label htmlFor="correct-field">Correct</label>
              <select
                id="correct-field"
                value={field}
                onChange={(event) => setField(event.target.value as typeof field)}
              >
                {CORRECTABLE.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field field--grow">
              <label htmlFor="correct-value">To</label>
              <input
                id="correct-value"
                required
                value={value}
                onChange={(event) => setValue(event.target.value)}
              />
            </div>
            <button className="button button--small" type="submit" disabled={busy}>
              Correct
            </button>
          </form>

          {changeList}
        </DetailSection>
      ) : changes.length > 0 ? (
        <DetailSection title="Corrections">
          {changeList}
        </DetailSection>
      ) : null}

      {mayErase ? (
        <div className="danger-zone">
          <p className="row__sub">Erasing removes the application and its resume for good.</p>
          <button
            type="button"
            className="button button--danger button--small"
            data-testid="erase-application"
            disabled={busy}
            onClick={() =>
              void attempt(
                () => platform.review.erase(organizationId, detail.id, 'requested in the dashboard'),
                onErased,
              )
            }
          >
            Erase this application and its resume
          </button>
        </div>
      ) : null}
    </div>
  );
}

function describe(error: unknown): string {
  if (error instanceof PlatformApiError) {
    // 409 carries the API's own wording, which explains what moved or why a
    // status change is not allowed. Replacing it with something vaguer would
    // throw away the only useful part.
    if (error.status === 409 || error.status === 400) return error.message;
    if (error.status === 403) return 'Your role may not do that.';
    if (error.status === 404) return 'That application is no longer there.';
  }
  return 'Something went wrong.';
}
